import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2, Plus, Trash2, Search } from "lucide-react";

const emptyItem = () => ({
  card_name: "",
  set_name: "",
  condition: "",
  quantity: 1,
  max_budget: 0,
  notes: "",
});

const CONDITIONS = ["Any", "Sealed", "Mint", "Near Mint", "Lightly Played", "Moderately Played", "Graded"];

export default function CardRequestPage() {
  const { user, isCustomer } = useAuth();
  const [form, setForm] = useState({
    customer_name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    items: [emptyItem()],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateItem = (idx, patch) =>
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (idx) =>
    setForm((f) => {
      const next = f.items.filter((_, i) => i !== idx);
      return { ...f, items: next.length ? next : [emptyItem()] };
    });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const cleaned = form.items
      .map((it) => ({
        card_name: (it.card_name || "").trim(),
        set_name: (it.set_name || "").trim(),
        condition: (it.condition || "").trim(),
        quantity: Math.max(1, parseInt(it.quantity, 10) || 1),
        max_budget: parseFloat(it.max_budget) || 0,
        notes: (it.notes || "").trim(),
      }))
      .filter((it) => it.card_name);
    if (cleaned.length === 0) {
      setError("Please add at least one card name.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/card-requests", {
        customer_name: form.customer_name,
        email: form.email,
        phone: form.phone,
        items: cleaned,
        message: form.message,
      });
      setDone(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="cardreq-success" className="max-w-2xl mx-auto px-4 sm:px-8 py-24 text-center">
        <CheckCircle2 className="w-16 h-16 text-red-600 mx-auto" />
        <h1 className="mt-6 font-[Outfit] text-4xl font-black text-slate-900">On the hunt!</h1>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Thanks <span className="text-slate-900 font-semibold">{done.customer_name}</span> — we&apos;ll
          keep an eye out for your list and email <span className="text-slate-900">{done.email}</span> the
          moment we find a match.
        </p>
        <div className="mt-8 bg-white border border-slate-200 rounded-xl p-6 text-left">
          <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-3">Your wishlist</div>
          <ul className="space-y-2">
            {done.items.map((it, i) => (
              <li key={i} className="flex items-center justify-between text-sm">
                <span className="text-slate-900 font-semibold">{it.card_name}</span>
                <span className="text-slate-500 font-mono">× {it.quantity}</span>
              </li>
            ))}
          </ul>
          <div className="text-[10px] text-slate-500 mt-3 pt-3 border-t border-slate-200 font-mono">
            Ref: {done.id}
          </div>
        </div>
        <Link to="/" className="mt-6 inline-block text-red-600 hover:text-red-700 font-mono text-sm uppercase tracking-wider">
          ← Back to home
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="cardreq-page" className="max-w-3xl mx-auto px-4 sm:px-8 py-16">
      <div className="text-xs tracking-[0.25em] uppercase font-mono text-red-600 mb-3">// Request cards</div>
      <h1 className="font-[Outfit] text-4xl sm:text-5xl font-black tracking-tighter text-slate-900">
        Chasing a card? <span className="text-red-600">Tell us.</span>
      </h1>
      <p className="mt-3 text-slate-600">
        Finishing a set? Hunting a specific pull? Drop your list here and we&apos;ll DM you the second we
        source one — through our vending network, restocks, or trades.
      </p>

      {!isCustomer && (
        <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
          <div className="text-sm text-slate-700">
            Returning customer?{" "}
            <Link to="/signin" className="text-red-600 font-bold hover:text-red-700">
              Sign in
            </Link>{" "}
            to skip typing your info every time.
          </div>
        </div>
      )}

      <form onSubmit={submit} className="mt-8 space-y-6">
        {/* Contact */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Your info</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full name" required>
              <input data-testid="cardreq-name" required value={form.customer_name} onChange={(e) => setField("customer_name", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Email" required>
              <input data-testid="cardreq-email" type="email" required value={form.email} onChange={(e) => setField("email", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Phone (optional)">
              <input data-testid="cardreq-phone" value={form.phone} onChange={(e) => setField("phone", e.target.value)} className={inputCls} />
            </Field>
          </div>
        </div>

        {/* Wishlist */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Cards you&apos;re after</h2>
            <span className="text-[10px] font-mono uppercase text-slate-500">
              {form.items.length} card{form.items.length === 1 ? "" : "s"}
            </span>
          </div>

          {form.items.map((it, idx) => (
            <div key={idx} data-testid={`cardreq-item-${idx}`} className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono uppercase tracking-wider text-red-600">Card {idx + 1}</span>
                {form.items.length > 1 && (
                  <button type="button" data-testid={`cardreq-remove-${idx}`} onClick={() => removeItem(idx)} className="text-red-500 hover:text-red-700 text-[10px] font-mono uppercase flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Card name" required>
                  <input data-testid={`cardreq-cardname-${idx}`} required value={it.card_name} onChange={(e) => updateItem(idx, { card_name: e.target.value })} placeholder="e.g. Charizard ex 199/165" className={inputCls} />
                </Field>
                <Field label="Set (optional)">
                  <input data-testid={`cardreq-setname-${idx}`} value={it.set_name} onChange={(e) => updateItem(idx, { set_name: e.target.value })} placeholder="e.g. 151, Obsidian Flames" className={inputCls} />
                </Field>
                <Field label="Condition wanted">
                  <select data-testid={`cardreq-condition-${idx}`} value={it.condition} onChange={(e) => updateItem(idx, { condition: e.target.value })} className={inputCls}>
                    <option value="">— any —</option>
                    {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Quantity">
                  <input data-testid={`cardreq-qty-${idx}`} type="number" min="1" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} className={inputCls} />
                </Field>
                <Field label="Max budget (optional)">
                  <input data-testid={`cardreq-budget-${idx}`} type="number" step="0.01" min="0" value={it.max_budget} onChange={(e) => updateItem(idx, { max_budget: e.target.value })} placeholder="0.00" className={inputCls} />
                </Field>
                <Field label="Notes">
                  <input data-testid={`cardreq-notes-${idx}`} value={it.notes} onChange={(e) => updateItem(idx, { notes: e.target.value })} placeholder="grade preference, language, etc." className={inputCls} />
                </Field>
              </div>
            </div>
          ))}

          <button type="button" data-testid="cardreq-add" onClick={addItem} className="w-full border border-dashed border-slate-300 hover:border-red-500 hover:text-red-600 text-slate-500 rounded-lg py-3 text-sm font-mono uppercase tracking-wider transition-colors flex items-center justify-center gap-2">
            <Plus className="w-4 h-4" /> Add another card
          </button>
        </div>

        <Field label="Anything else? (deadline, quantities, alt versions)">
          <textarea data-testid="cardreq-message" rows={3} value={form.message} onChange={(e) => setField("message", e.target.value)} className={inputCls} />
        </Field>

        {error && (
          <div data-testid="cardreq-error" className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-md text-sm">
            {error}
          </div>
        )}

        <button type="submit" disabled={submitting} data-testid="cardreq-submit" className="w-full bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-4 rounded-md uppercase tracking-wide transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
          <Search className="w-4 h-4" />
          {submitting ? "Sending…" : "Send my request"}
        </button>
        <p className="text-xs text-slate-500 text-center">
          We&apos;ll email you when we find a match — no pressure to buy.
        </p>
      </form>
    </div>
  );
}

const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-red-500";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5">
        {label} {required && <span className="text-red-600">*</span>}
      </span>
      {children}
    </label>
  );
}
